import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardAvatar, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin, mergeMap } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { StoreConfigService } from 'app/pages/store-management/setting/store-config.service';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogComponent, ConfirmDialogData } from 'app/shared/dialog/confirm-dialog.component';
import {
    BuiltInExternalPhotoSourceConfig,
    BuiltInExternalPhotoSourceDefinition,
    DirectPhotoSource,
    ExternalPhotoSourceStatus,
    GenericExternalPhotoSourceConfig,
    GlobalPermission,
    SessionPhotoSource,
    StockPhotoSourceConfig,
    UpdateBuiltInExternalPhotoSourceInput,
    UpdateGenericExternalPhotoSourceInput,
    UpdateSessionPhotoSourceInput,
} from 'app/shared/graph';
import { StoreLocalesService } from 'app/shared/store-locales/store-locales.service';
import { TranslateDialogComponent, TranslateDialogData, TranslateDialogResult } from 'app/shared/store-locales/translate-dialog.component';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';

import { DirectPhotoSourceAddDialogComponent } from './direct-photo-source-add-dialog.component';
import { ExternalPhotosConfigService, PhotoSourceBasicSettings } from './external-photos-config.service';
import { GenericExternalPhotoSourceAddDialogComponent } from './generic-external-photo-source-add-dialog.component';
import { PhotoSourceActionsMenuComponent } from './photo-source-actions-menu.component';
import { SessionPhotoSourceAddDialogComponent } from './session-photo-source-add-dialog.component';
import { StockPhotoSourceAddDialogComponent, StockPhotoSourceAddDialogData, StockPhotoSourceAddDialogResult } from './stock-photo-source-add-dialog.component';
import { StockPhotoSourcesService } from './stock-photo-sources.service';

@Component({
    templateUrl: './external-photo-sources-page.component.html',
    styleUrls: ['./external-photo-sources-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, ErrorComponent, MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, CopyToClipboardComponent, MatCardAvatar, MatIcon, MatCardContent, MatButton, RouterLink, MatCheckbox, FormsModule, MatCardActions, SaveButtonComponent, MatExpansionPanelTitle, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, PhotoSourceActionsMenuComponent],
})
export class ExternalPhotoSourcesPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly authorizationService = inject(AuthorizationService);
    private readonly route = inject(ActivatedRoute);
    private readonly photosConfigService = inject(ExternalPhotosConfigService);
    private readonly stockPhotoSourcesService = inject(StockPhotoSourcesService);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);
    private readonly storeConfigService = inject(StoreConfigService);
    private readonly storeLocalesService = inject(StoreLocalesService);

    public storeId: string;

    photoSourceBasicSettings: PhotoSourceBasicSettings = {
        isExternalUploaderEnabled: false,
        isRecentUploadsEnabled: false,
        isPastUploadsEnabled: false,
    };
    builtinsExternalPhotoSourceDefinitions: Array<BuiltInExternalPhotoSourceDefinition> = [];
    builtInExternalPhotoSources: Array<BuiltInExternalPhotoSourceConfig> = [];
    genericExternalPhotoSources: Array<GenericExternalPhotoSourceConfig> = [];
    stockPhotoSources: Array<StockPhotoSourceConfig> = [];
    directPhotoSources: Array<DirectPhotoSource> = [];
    sessionPhotoSources: Array<SessionPhotoSource> = [];

    readonly labelTranslations = signal<Record<string, Record<string, string>>>({});

    private authorizationContext: AuthorizationContext;

    ngOnInit(): void {
        this.registerSubscription(this.authorizationService.authorizationContext().subscribe(authorizationContext => {
            this.authorizationContext = authorizationContext;
        }));

        this.registerSubscription(this.authorizationService.authorizationContext().subscribe(authorizationContext => {
            this.authorizationContext = authorizationContext;

            this.route.params.subscribe(params => {
                this.storeId = params.storeId;

                let externalPhotoSourceObservable = this.photosConfigService.loadExternalPhotoSources(this.storeId);
                let sessionPhotoSourcesObservable = this.photosConfigService.loadSessionPhotoSources(this.storeId);
                let localizedLabelsObservable = forkJoin([externalPhotoSourceObservable, sessionPhotoSourcesObservable])
                    .pipe(mergeMap(([externalSources, sessionSources]) => this.storeLocalesService.getStoreLocalizedLabelsAsRecord(
                        this.storeId,
                        [
                            ...externalSources.stockPhotoSources.map(r => this.getStockPhotoLocalizationKey(r)),
                            ...sessionSources.map(s => this.getSessionPhotoLocalizationKey(s)),
                        ],
                    )));
                this.subscribeWithGenericLoadingErrorHandling(forkJoin([
                        this.photosConfigService.loadBuiltInExternalPhotoSourceDefinitions(),
                        externalPhotoSourceObservable,
                        this.photosConfigService.loadDirectPhotoSources(this.storeId),
                        this.photosConfigService.getPhotoSourceBasicSetting(this.storeId),
                        sessionPhotoSourcesObservable,
                        localizedLabelsObservable,
                    ]),
                    ([builtinsExternalPhotoSourceDefinitions, externalPhotoSources, directPhotoSources, photoSourceBasicSettings, sessionPhotoSources, labelTranslations]) => {
                        this.builtinsExternalPhotoSourceDefinitions = builtinsExternalPhotoSourceDefinitions;
                        this.builtInExternalPhotoSources = externalPhotoSources.builtInExternalPhotoSources;
                        this.genericExternalPhotoSources = externalPhotoSources.genericExternalPhotoSources;
                        this.stockPhotoSources = externalPhotoSources.stockPhotoSources;
                        this.directPhotoSources = directPhotoSources;
                        this.pageStatus = PageStatus.loaded;
                        this.photoSourceBasicSettings = photoSourceBasicSettings;
                        this.sessionPhotoSources = sessionPhotoSources;

                        this.labelTranslations.set(labelTranslations);
                    });
            });
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    // region Basic Settings

    saveBasicSettings() {
        this.subscribeWithGenericSavingErrorHandling(this.storeConfigService.updateStoreConfig({
                storeId: this.storeId,
                ...this.photoSourceBasicSettings,
            }),
        );
    }

    // endregion

    // region Built-in External Photo Source

    openBuiltInPhotoSourceEditor(providerId: string): void {
        this.router.navigate(['built-in', 'edit', providerId], { relativeTo: this.route });
    }

    openAddBuiltInPhotoSourceEditor(providerId: string): void {
        this.router.navigate(['built-in', 'create', providerId], { relativeTo: this.route });
    }

    isProviderExistsForStore(providerId: string): boolean {
        return !!this.builtInExternalPhotoSources.find(
            (x) => x.photoSourceId === providerId,
        );
    }

    switchBuiltInSourceEnabled(
        source: BuiltInExternalPhotoSourceConfig,
        status: ExternalPhotoSourceStatus,
    ): void {
        const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            data: {
                title: 'Update photo source',
                message: `Do you really want to ${
                    status === ExternalPhotoSourceStatus.Enabled ? 'enable' : 'disable'
                } the photo source ${source.label} ?`,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.updateBuiltInExternalPhotoSource(source, {
                storeId: this.storeId,
                photoSourceId: source.photoSourceId,
                status: status,
            });
        });
    }

    disableBuiltInPhotoSource(source: BuiltInExternalPhotoSourceConfig): void {
        this.switchBuiltInSourceEnabled(source, ExternalPhotoSourceStatus.Disabled);
    }

    enableBuiltInPhotoSource(source: BuiltInExternalPhotoSourceConfig): void {
        this.switchBuiltInSourceEnabled(source, ExternalPhotoSourceStatus.Enabled);
    }

    deleteBuiltInPhotoSource(source: BuiltInExternalPhotoSourceConfig): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update photo source',
                message: `Do you really want to delete the photo source ${source.label} ?`,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.photosConfigService.deleteBuiltInExternalPhotoSource({
                storeId: this.storeId,
                photoSourceId: source.photoSourceId,
            }).subscribe({
                next: () => {
                    let index = this.builtInExternalPhotoSources.findIndex(s => s.photoSourceId === source.photoSourceId);
                    if (index !== -1) {
                        this.builtInExternalPhotoSources.splice(index, 1);
                    }
                },
                error: err => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
        });
    }

    private updateBuiltInExternalPhotoSource(source: BuiltInExternalPhotoSourceConfig, input: UpdateBuiltInExternalPhotoSourceInput) {
        this.photosConfigService.updateBuiltInExternalPhotoSource(input)
            .subscribe({
                next: (updatedSource) => {
                    source.appId = updatedSource.appId;
                    source.details = updatedSource.details;
                    source.icon = updatedSource.icon;
                    source.label = updatedSource.label;
                    source.photoSourceId = updatedSource.photoSourceId;
                    source.status = updatedSource.status;
                    source.store = updatedSource.store;
                },
                error: (err) => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
    }

    // endregion

    // region Generic External Photo Source

    editGenericExternalPhotoSource(source: GenericExternalPhotoSourceConfig): void {
        this.router.navigate(['generic', 'edit', source.photoSourceId], { relativeTo: this.route });
    }

    openAddGenericExternalPhotoSourceDialog(): void {
        const dialogRef = this.dialog.open<GenericExternalPhotoSourceAddDialogComponent, any, string>(GenericExternalPhotoSourceAddDialogComponent);
        dialogRef.afterClosed().subscribe((photoSourceId) => {
            if (!photoSourceId) {
                return;
            }

            this.router.navigate(['generic', 'create', photoSourceId], { relativeTo: this.route });
        });
    }

    switchGenericSourceStatus(
        source: GenericExternalPhotoSourceConfig,
        status: ExternalPhotoSourceStatus,
    ): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update photo source',
                message: `Do you really want to ${
                    status === ExternalPhotoSourceStatus.Enabled ? 'enable' : 'disable'
                } the photo source ${source.label} ?`,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.updateGenericExternalPhotoSource(source, {
                storeId: source.store.id,
                photoSourceId: source.photoSourceId,
                status: status,
            });
        });
    }

    disableGenericPhotoSource(source: GenericExternalPhotoSourceConfig): void {
        this.switchGenericSourceStatus(source, ExternalPhotoSourceStatus.Disabled);
    }

    enableGenericPhotoSource(source: GenericExternalPhotoSourceConfig): void {
        this.switchGenericSourceStatus(source, ExternalPhotoSourceStatus.Enabled);
    }

    deleteGenericPhotoSource(source: GenericExternalPhotoSourceConfig): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update photo source',
                message: `Do you really want to delete the photo source ${source.label} ?`,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.photosConfigService.deleteGenericExternalPhotoSource({
                storeId: this.storeId,
                photoSourceId: source.photoSourceId,
            }).subscribe({
                next: () => {
                    let index = this.genericExternalPhotoSources.findIndex(s => s.photoSourceId === source.photoSourceId);
                    if (index !== -1) {
                        this.genericExternalPhotoSources.splice(index, 1);
                    }
                },
                error: err => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
        });
    }

    private updateGenericExternalPhotoSource(source: GenericExternalPhotoSourceConfig, input: UpdateGenericExternalPhotoSourceInput) {
        this.photosConfigService.updateGenericExternalPhotoSource(input)
            .subscribe({
                next: (updatedSource) => {
                    source.details = updatedSource.details;
                    source.appId = updatedSource.appId;
                    source.icon = updatedSource.icon;
                    source.label = updatedSource.label;
                    source.photoSourceId = updatedSource.photoSourceId;
                    source.status = updatedSource.status;
                    source.store = updatedSource.store;
                    source.authorizationUrl = updatedSource.authorizationUrl;
                    source.photoUrl = updatedSource.photoUrl;
                    source.albumUrl = updatedSource.albumUrl;
                    source.tokenUrl = updatedSource.tokenUrl;
                },
                error: (err) => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
    }

    // endregion

    // region StockPhotoSources

    canManageStockPhotoSource(): boolean {
        return this.authorizationContext.hasGlobalPermissions(GlobalPermission.ManageStockPhotoSources);
    }

    openStockPhotoSourceAddDialog(): void {
        let currentStockPhotoSourceIds = this.stockPhotoSources.map(s => s.id);
        let dialogData = {
            alreadyMapped: currentStockPhotoSourceIds,
        } as StockPhotoSourceAddDialogData;
        const dialogRef = this.dialog.open<StockPhotoSourceAddDialogComponent, StockPhotoSourceAddDialogData, StockPhotoSourceAddDialogResult>(
            StockPhotoSourceAddDialogComponent,
            {
                data: dialogData,
            },
        );
        dialogRef.afterClosed().subscribe((result: StockPhotoSourceAddDialogResult | undefined) => {
            if (!result) {
                return;
            }

            if (!result.photoSourceId) {
                return;
            }

            let index = this.stockPhotoSources.findIndex(s => s.id === result.photoSourceId);
            if (index !== -1) {
                return;
            }

            this.subscribeWithGenericSavingErrorHandling(this.stockPhotoSourcesService.addToStore({
                storeId: this.storeId,
                photoSourceId: result.photoSourceId,
            }), () => {
                this.stockPhotoSourcesService.load(result.photoSourceId).subscribe(source => {
                    this.stockPhotoSources.push(source);
                });

            });
        });
    }

    editStockPhotoSource(source: StockPhotoSourceConfig): void {
        this.router.navigate([`/stock-photo-sources/${source.id}`]);
    }

    deleteStockPhotoSource(source: StockPhotoSourceConfig) {
        const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            data: {
                title: 'Remove photo source',
                message: `Removing this source will break all projects needing it to be opened and/or rendered. We recommend to disable this source first.`,
                subMessage: `Do you really want to remove the photo source '${source.label ?? source.id}'?`,
                options: {
                    yes: {
                        label: 'Remove',
                        warn: true,
                    },
                },
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.stockPhotoSourcesService.removeFromStore({
                storeId: this.storeId,
                photoSourceId: source.id,
            }).subscribe({
                next: () => {
                    let index = this.stockPhotoSources.findIndex(s => s.id === source.id);
                    if (index !== -1) {
                        this.stockPhotoSources.splice(index, 1);
                    }
                },
                error: err => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
        });
    }

    translateStockPhotoSource(source: StockPhotoSourceConfig): void {
        this.subscribeWithGenericLoadingErrorHandling(
            forkJoin([
                this.storeLocalesService.getStoreLocalizedLabel(this.storeId, this.getStockPhotoLocalizationKey(source)),
                this.storeLocalesService.listStoreSupportedLocales(this.storeId),
            ]), ([labelTranslations, supportedLocales]) => {

                const dialogRef = this.dialog.open<TranslateDialogComponent, TranslateDialogData, TranslateDialogResult>(TranslateDialogComponent, {
                    data: {
                        storeId: this.storeId,
                        title: `Labels for ${source.id}`,
                        label: labelTranslations['en'],
                        supportedLocales: supportedLocales,
                        labelTranslations: labelTranslations,
                    } as TranslateDialogData,
                });
                dialogRef.afterClosed().subscribe(result => {
                    if (!result?.labelTranslations) {
                        return;
                    }

                    this.storeLocalesService.updateStoreLocalizedLabel(
                        this.storeId,
                        this.getStockPhotoLocalizationKey(source),
                        result.labelTranslations,
                    ).subscribe(() => {
                        this.labelTranslations()[this.getStockPhotoLocalizationKey(source)] = result.labelTranslations;
                    });
                });

            },
        );
    }

    getStockPhotoLocalizationKey(stockPhotoSource: StockPhotoSourceConfig): string {
        return `hub.sources.stock.${stockPhotoSource.id}.label`;
    }

    getSessionPhotoLocalizationKey(sessionPhotoSource: SessionPhotoSource): string {
        return `hub.sources.session.${sessionPhotoSource.name}.label`;
    }

    // endregion

    // region Direct Photo Source

    openDirectPhotoSourceAddDialog(): void {
        const dialogRef = this.dialog.open<DirectPhotoSourceAddDialogComponent, any, string>(DirectPhotoSourceAddDialogComponent);
        dialogRef.afterClosed().subscribe((photoSourceId) => {
            if (!photoSourceId) {
                return;
            }

            this.router.navigate(['direct', 'create', photoSourceId], { relativeTo: this.route });
        });

    }

    openDirectPhotoSourceEditor(directPhotoSourceId: string): void {
        this.router.navigate(['direct', 'edit', directPhotoSourceId], { relativeTo: this.route });
    }

    // endregion

    // region Session Photo Source

    openSessionPhotoSourceAddDialog(): void {
        const dialogRef = this.dialog.open<SessionPhotoSourceAddDialogComponent, any, { photoSourceId: string, thumbnailUrl: string }>(SessionPhotoSourceAddDialogComponent);
        dialogRef.afterClosed().subscribe((photoSourceId) => {
            if (!photoSourceId) {
                return;
            }
            this.router.navigate(['session', 'create', photoSourceId], { relativeTo: this.route });
        });
    }

    openSessionPhotoSourceEditor(sessionPhotoSourceId: string): void {
        this.router.navigate(['session', 'edit', sessionPhotoSourceId], { relativeTo: this.route });
    }

    switchSessionSourceStatus(
        source: SessionPhotoSource,
        status: ExternalPhotoSourceStatus,
    ): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update photo source',
                message: `Do you really want to ${
                    status === ExternalPhotoSourceStatus.Enabled ? 'enable' : 'disable'
                } the photo source ${source.name} ?`,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.updateSessionPhotoSource(source,
                {
                    storeId: this.storeId,
                    name: source.name,
                    thumbnailUrl: source.thumbnailUrl,
                    enabled: status === ExternalPhotoSourceStatus.Enabled,
                });
        });
    }

    disableSessionPhotoSource(source: SessionPhotoSource): void {
        this.switchSessionSourceStatus(source, ExternalPhotoSourceStatus.Disabled);
    }

    enableSessionPhotoSource(source: SessionPhotoSource): void {
        this.switchSessionSourceStatus(source, ExternalPhotoSourceStatus.Enabled);
    }

    private updateSessionPhotoSource(source: SessionPhotoSource, input: UpdateSessionPhotoSourceInput) {
        this.photosConfigService.updateSessionPhotoSource(input)
            .subscribe({
                next: (updatedSource) => {
                    source.enabled = !source.enabled;
                },
                error: (err) => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = err;
                },
            });
    }

    // endregion
}
